import React, { useState, useRef, useEffect, useCallback } from "react";
import { MapPin, Check, AlertTriangle, Loader2 } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

// value: { address, lat, lon, verified }  onChange(next)
export function AddressAutocomplete({ value, onChange, placeholder, testId, iconColor = "#E8A33D" }) {
  const { lang } = useLang();
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [manual, setManual] = useState(false);
  const timer = useRef(null);

  const address = value?.address || "";
  const verified = !!value?.verified;

  const fetchSuggestions = useCallback(async (q) => {
    if (q.trim().length < 3) { setSuggestions([]); return; }
    setLoading(true);
    const ctrl = new AbortController();
    const to = setTimeout(() => ctrl.abort(), 7000);
    try {
      const res = await fetch(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(q)}&limit=5&autocomplete=1`, { signal: ctrl.signal });
      const data = await res.json();
      setSuggestions(data.features || []);
    } catch { setSuggestions([]); }
    finally { clearTimeout(to); setLoading(false); }
  }, []);

  const onType = (text) => {
    onChange({ address: text, lat: null, lon: null, verified: false });
    setOpen(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => fetchSuggestions(text), 300);
  };

  const select = (f) => {
    const [lon, lat] = f.geometry.coordinates;
    const p = f.properties;
    onChange({ address: p.label, lat, lon, postcode: p.postcode, city: p.city, verified: true });
    setSuggestions([]);
    setOpen(false);
    setManual(false);
  };

  useEffect(() => () => timer.current && clearTimeout(timer.current), []);

  return (
    <div className="relative">
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: iconColor }} />
        <input
          data-testid={testId}
          value={address}
          onChange={(e) => onType(e.target.value)}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 180)}
          placeholder={placeholder}
          className={`w-full rounded-2xl border bg-white pl-9 pr-9 py-3 text-sm outline-none transition-colors ${verified ? "border-sage" : "border-line focus:border-amber"}`}
        />
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-ink/40" />}
        {!loading && verified && <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-sage" data-testid={`${testId}-verified`} />}
      </div>

      {open && (suggestions.length > 0 || (address.trim().length >= 3 && !loading)) && (
        <div className="absolute z-30 left-0 right-0 mt-1 rounded-2xl border border-line bg-white shadow-lg overflow-hidden">
          {suggestions.map((f) => (
            <button
              key={f.properties.id}
              type="button"
              data-testid={`${testId}-option`}
              onMouseDown={(e) => { e.preventDefault(); select(f); }}
              className="w-full text-left px-4 py-2.5 text-sm hover:bg-paper border-b border-line last:border-0"
            >
              <div className="font-medium">{f.properties.label}</div>
            </button>
          ))}
          {suggestions.length === 0 && address.trim().length >= 3 && (
            <div className="px-4 py-3 text-xs text-ink/50">
              {lang === "fr" ? "Aucune adresse trouvée. Précisez le numéro, la rue et la ville." : "No address found. Add the number, street and city."}
              <button type="button" onMouseDown={(e) => { e.preventDefault(); setManual(true); onChange({ address, lat: null, lon: null, verified: false, unverified: true }); setOpen(false); }} className="block mt-1.5 font-semibold text-amber underline" data-testid={`${testId}-manual`}>
                {lang === "fr" ? "Saisir quand même (non vérifiée)" : "Enter anyway (unverified)"}
              </button>
            </div>
          )}
        </div>
      )}
      {value?.unverified && !verified && (
        <p className="flex items-center gap-1.5 text-xs text-coral mt-1.5">
          <AlertTriangle className="w-3.5 h-3.5" /> {lang === "fr" ? "Adresse non vérifiée" : "Unverified address"}
        </p>
      )}
    </div>
  );
}
